import { type TopicIdentityRecord } from "@/shared/domain/topics";
import {
  type BatchResponse,
  HttpClient,
  type PaginatedResponse,
  robotoHeaders,
} from "@/shared/http";
import { bigintReplacer, parseTimestampsAsBigInt } from "@/shared/json";

import { type SessionFile } from "./SessionFile";
import {
  type CreateSessionRequest,
  type SessionFileDeclaration,
  type SessionRecord,
  type UpdateSessionRequest,
} from "./SessionRecord";

/**
 * Options for requests to `/v1/sessions/id/<id>`. The server authorizes these
 * against the org that owns the session, so they carry no org header.
 */
interface RequestOptions {
  abortSignal?: AbortSignal;
}

interface ListOptions extends RequestOptions {
  pageToken?: string;
}

interface FileListOptions extends ListOptions {
  /** Sent as `X-Roboto-Connection-Consistency`. `strongly_consistent` reads from the database writer,
   * so the listing reflects adds that just completed; unset, the route may read a lagging replica. */
  consistency?: "eventually_consistent" | "strongly_consistent";
  limit?: number;
}

/** The add-files route accepts at most MAX_FILES_AND_TOPICS_PER_REQUEST files and topics together
 * (packages/roboto/src/roboto/experimental/ingest/operations.py). This client declares no topics, so
 * the whole allowance goes to files. */
export const MAX_FILES_PER_ADD_FILES_REQUEST = 500;

interface CreateSessionOptions {
  abortSignal?: AbortSignal;
  /**
   * Sets the `X-Roboto-Org-Id` header. Required for callers whose Cognito
   * identity is a member of more than one org: the backend
   * `REQUIRES_CALLER_ORG` rule rejects ambiguous requests with
   * `RobotoNoOrgProvidedException`.
   */
  orgId?: string;
}

/**
 * HTTP client for the `/v1/sessions` API. Construct once per
 * `HttpClient`; resolved by feature code through the
 * `DomainServicesProvider` context.
 */
export class SessionService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /**
   * Add files to an existing session via `POST /v1/sessions/id/<id>/files`. The
   * server adds every entry it does not refuse, so a call can come back part
   * applied: the response holds one element per file, in request order,
   * carrying either that file's row in the session's file listing or why the
   * server refused it. A failure the server did not anticipate, such as a
   * timeout, adds none of the entries. Adding a file the session already holds
   * replaces the window recorded for it.
   */
  public async addFilesToSession(
    sessionId: string,
    files: SessionFileDeclaration[],
    options?: RequestOptions,
  ): Promise<BatchResponse<SessionFile>> {
    const url = this.#httpClient.constructUrl(
      `v1/sessions/id/${sessionId}/files`,
    );
    // The server upserts re-added files, so a retried request converges.
    const response = await this.#httpClient.post(url, {
      idempotent: true,
      // bigintReplacer keeps the window bounds full-precision on the wire.
      body: JSON.stringify({ files }, bigintReplacer),
      signal: options?.abortSignal,
    });
    return response.json<BatchResponse<SessionFile>>(parseTimestampsAsBigInt);
  }

  /**
   * Create a session via `POST /v1/sessions`. The session starts holding no
   * files; add them in a follow-up {@link addFilesToSession} call.
   * `createSessionWithFiles` chains the two and deletes the new session when
   * any of its files did not go in.
   */
  public async createSession(
    request: CreateSessionRequest,
    options?: CreateSessionOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl("v1/sessions");
    // Non-idempotent on the server (each POST mints a new session_id), so a
    // transient 502 must not be silently retried into a duplicate session.
    const response = await this.#httpClient.post(url, {
      idempotent: false,
      headers: options?.orgId
        ? robotoHeaders({ orgId: options.orgId })
        : undefined,
      body: JSON.stringify(request),
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }

  public async deleteSession(
    sessionId: string,
    options?: RequestOptions,
  ): Promise<void> {
    const url = this.#httpClient.constructUrl(`v1/sessions/id/${sessionId}`);
    const response = await this.#httpClient.delete(url, {
      idempotent: false,
      signal: options?.abortSignal,
    });
    await response.throwIfError();
  }

  public async getSessionById(
    sessionId: string,
    options?: RequestOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl(`v1/sessions/id/${sessionId}`);
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }

  /**
   * List every file in a session, following `next_token` until the server
   * reports no more pages.
   *
   * `limit` sets the page size for each request in the chain; the route
   * defaults to 100 and rejects a page size above 1000.
   */
  public async listAllSessionFiles(
    sessionId: string,
    options?: Omit<FileListOptions, "pageToken">,
  ): Promise<SessionFile[]> {
    const allFiles: SessionFile[] = [];
    let pageToken: string | undefined = undefined;

    do {
      const page = await this.listSessionFiles(sessionId, {
        ...options,
        pageToken,
      });
      allFiles.push(...page.items);
      pageToken = page.next_token ?? undefined;
    } while (pageToken);

    return allFiles;
  }

  /**
   * List the IDs of every device attached to a session: devices attached
   * directly, plus those the backend infers from the `device_id` on the
   * session's files or their datasets. Follows `next_token` until the
   * server reports no more pages.
   */
  public async listAllSessionDevices(
    sessionId: string,
    options?: Omit<ListOptions, "pageToken">,
  ): Promise<string[]> {
    const allDeviceIds: string[] = [];
    let pageToken: string | undefined = undefined;

    do {
      const searchParams = new URLSearchParams();
      if (pageToken !== undefined) {
        searchParams.append("page_token", pageToken);
      }
      const url = this.#httpClient.constructUrl(
        `v1/sessions/id/${sessionId}/devices`,
        searchParams,
      );
      const response = await this.#httpClient.get(url, {
        signal: options?.abortSignal,
      });
      const page = await response.json<PaginatedResponse<string>>();
      allDeviceIds.push(...page.items);
      pageToken = page.next_token ?? undefined;
    } while (pageToken);

    return allDeviceIds;
  }

  public async listSessionFiles(
    sessionId: string,
    options?: FileListOptions,
  ): Promise<PaginatedResponse<SessionFile>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    if (options?.limit !== undefined) {
      searchParams.append("page_size", String(options.limit));
    }
    const url = this.#httpClient.constructUrl(
      `v1/sessions/id/${sessionId}/files`,
      searchParams,
    );
    const response = await this.#httpClient.get(url, {
      headers: options?.consistency
        ? { "X-Roboto-Connection-Consistency": options.consistency }
        : undefined,
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<SessionFile>>(
      parseTimestampsAsBigInt,
    );
  }

  public async listSessionTopics(
    sessionId: string,
    options?: ListOptions,
  ): Promise<PaginatedResponse<TopicIdentityRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    const url = this.#httpClient.constructUrl(
      `v1/sessions/id/${sessionId}/topics`,
      searchParams,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<TopicIdentityRecord>>();
  }

  public async updateSession(
    sessionId: string,
    updates: UpdateSessionRequest,
    options?: RequestOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl(`v1/sessions/id/${sessionId}`);
    const response = await this.#httpClient.put(url, {
      idempotent: true,
      body: JSON.stringify(updates),
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }
}
